import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { Avatar, Badge, Box, Divider, Icon, IconButton, List, ListItemIcon, ListSubheader, Menu, MenuItem, Tooltip, Typography } from '@mui/material';

import { NavItem } from '../../models/navItem';
import { MijnOrganisatieListItem } from '../../models/user/mijnOrganisatieListItem';
import MijnOrganisatiesNavMenu from '../authentication/user/MijnOrganisatiesNavMenu';

import { authService } from '../../services/auth.service';
import { getRoleLabel } from '../../models/user/userRole';
import { userManagementService } from '../../services/user-management.service';
import { navigationService } from '../../services/navigation.service';
import { useUnreadNotificationsCount } from '../../../hooks/useUnreadNotificationsCount';


const NavHeader: React.FC = () => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const account = accounts[0];
  const isAuthenticated = useIsAuthenticated();
  const { count: unreadCount } = useUnreadNotificationsCount();
  
  const [menuItems, setMenuItems] = useState<NavItem[]>([]);
  const [extendedMenuItems, setExtendedMenuItems] = useState<NavItem[]>([]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [initials, setInitials] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [organisatie, setMijnOrganisatie] = useState<MijnOrganisatieListItem | null>(null);

  const location = "header";

  const fetchMenuItems = async () => {
    const items = await navigationService.getNavigationItems(location);
    setMenuItems(items);

    extendMenuItems();
  };

  const fetchInitials = async () => {
    const fetchedInitials = await authService.getInitials();
    setInitials(fetchedInitials);
  };

  const getUserRole = async () => {
    const role = await authService.getRole();
    setUserRole(role);
  };

  const fetchMijnOrganisaties = async () => {
    // setLoading(true);
    try {
      const storedOrganisatie = userManagementService.getStoredOrganisatie();
      if (storedOrganisatie) {
        setMijnOrganisatie(storedOrganisatie);
      }
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      // setError(error.message || 'Er is een fout opgetreden');
      // setLoading(false);
    }
  };

  useEffect(() => {
    getUserRole();

    fetchMenuItems();
    fetchInitials();
    fetchMijnOrganisaties();
  }, [isAuthenticated, account]);

  const openAccountMenu = Boolean(anchorEl);
  const onClickShowAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onClickNavigate = (path: string) => {
    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer');
    }
    else {
      navigate(path);
    }
    handleClose();
  };
  
  const extendMenuItems = () => {
    const items = [...menuItems];

    if (organisatie && authService.isKeyUser()) {
      items.push({
        path: `/mijn/organisatie/${organisatie.id}`,
        name: `${organisatie.displayName} gebruikers`,
        component: '',
        public: false,
        roles: ['Admin', 'KeyUser'],
        icon: 'domain',
        navigation: ['header'],
        order: { header: 46 }
      });
    }

    // Sorteer op order.header
    items.sort((a, b) => {
      const orderA = a?.order?.header ?? 0;
      const orderB = b?.order?.header ?? 0;
      return orderA - orderB;
    });

    setExtendedMenuItems(items);
  };

  return (
    <div className="navigation nav-header">
      <Box sx={{ display: 'flex', alignItems: 'left', textAlign: 'left' }}>
        {isAuthenticated && <MijnOrganisatiesNavMenu />}
        {isAuthenticated && (
          <Tooltip title={
            <>
              Open het berichtencentrum.
              {unreadCount > 0 && (
                <>
                  <br />
                  Er zijn nog {unreadCount} ongelezen berichten
                </>
              )}
            </>
          }>
            <IconButton
              onClick={() => navigate('/berichtencentrum')}
              className="btn-nav-header btn-notifications"
              size="large"
              aria-label="Ga naar berichtencentrum"
              sx={{
                ...(unreadCount > 0 && {
                  backgroundColor: 'rgba(237, 108, 2, 0.15) !important',
                  '&:hover': {
                    backgroundColor: 'rgba(237, 108, 2, 0.3) !important',
                  },
                }),
              }}
            >
              <Badge
                badgeContent={unreadCount}
                color="warning"
                max={99}
                sx={{ '& .MuiBadge-badge': { top: -4, right: -4 } }}
              >
                <Icon fontSize="inherit">notifications</Icon>
              </Badge>
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={isAuthenticated ? `Account van ${account?.name}` : 'Account'}>
          <IconButton
            onClick={onClickShowAccountMenu}
            className={`btn-nav-header btn-account-menu`}
            // className={`btn-nav-header ${isAuthenticated ? 'btn-user-menu' : 'btn-account-menu'}`}
            size="large"
            sx={{ ml: 2 }}
            aria-controls={openAccountMenu ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={openAccountMenu ? 'true' : undefined}
          >
            <Icon fontSize="inherit">account_circle</Icon>
            {/* {isAuthenticated ? (
              <Avatar sx={{ width: 48, height: 48, padding: 0 }} className="purple">{initials}</Avatar>
            ) : (
              <Icon fontSize="inherit">account_circle</Icon>
            )} */}
          </IconButton>
        </Tooltip>
      </Box>
  
      <Menu
        id="account-menu"
        className="navigation nav-menu"
        anchorEl={anchorEl}
        open={openAccountMenu}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {isAuthenticated ? (
          <>
            {account && (
              <MenuItem disabled style={{ height: '90px' }}>
                <div>
                  <b>{account?.name}</b><br />
                  {account?.username.toLowerCase()}<br />
                  {organisatie ? (
                    <>
                      Type: {getRoleLabel(organisatie.userType)}<br />
                      Actieve organisatie: {organisatie.displayName}<br />
                    </>
                  ) : (
                    <>
                      Er is nog geen actieve organisatie gekozen
                    </>
                  )}
                </div>
              </MenuItem>
            )}
            <List
              sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}
              component="nav"
              aria-labelledby="nested-list-subheader-settings"
              subheader={
                <ListSubheader component="div" id="nested-list-subheader-settings" />
              }
            >
              {extendedMenuItems.map(({ path, name, icon, order, component }, index) =>
                path === "" && name === "divider" ? (
                  <Divider key={`divider-${index}`} className={`menu-order-${order?.header ?? ''} `} />
                ) : component === 'title' ? (
                  <ListSubheader
                    key={`title-${index}`}
                    component="div"
                    disableSticky
                    className={`menu-order-${order?.header ?? ''}`}
                    sx={{
                      lineHeight: 1.6,
                      bgcolor: 'transparent',
                      px: 2,
                      pt: 0.5,
                      pb: 0,
                    }}
                  >
                    <Typography variant="overline" color="text.secondary">
                      {name}
                    </Typography>
                  </ListSubheader>
                ) : (
                  <MenuItem key={path} onClick={() => onClickNavigate(path)}>
                    <ListItemIcon>
                      <Icon>{icon}</Icon>
                    </ListItemIcon>
                    {name}
                  </MenuItem>
                )
              )}
            </List>
          </>
        ) : (
          <MenuItem onClick={() => onClickNavigate('/inloggen')}>
            <ListItemIcon>
              <Icon>lock_open</Icon>
            </ListItemIcon>
            Inloggen
          </MenuItem>
        )}
      </Menu>
    </div>
  );
}

export default NavHeader;
